import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn, dateLocale } from "@/lib/utils";
import { fmtDate } from "@/pages/Reperes";

import { translateFr as tfr } from "@/i18n";
function Modal({ title, children, onClose, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={cn("w-full rounded-2xl border border-border bg-card p-4 space-y-3 max-h-[85vh] overflow-y-auto", wide ? "max-w-3xl" : "max-w-md")}
        onClick={(e) => e.stopPropagation()} data-testid={testid} role="dialog" aria-label={title}>
        <h3 className="text-sm font-bold">{title}</h3>
        {children}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block text-xs">
    <span className="text-muted-foreground">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
const inputCls = "w-full h-9 rounded-lg border border-border bg-background px-2.5 text-xs focus:outline-none focus:ring-1 focus:ring-athlynx/50";
const Footer = ({ onClose, onSave, busy, saveLabel, tf, danger, testid }) => (
  <div className="flex justify-end gap-2 pt-1">
    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={onClose}>{tf("Annuler")}</Button>
    <Button size="sm" disabled={busy} onClick={onSave} data-testid={testid}
      className={cn("h-8 text-xs", danger ? "bg-athlynx hover:bg-athlynx/90 text-white" : "bg-foreground hover:bg-foreground/90 text-background")}>
      {saveLabel}
    </Button>
  </div>
);

export function CareerProjectDialog({ pid, project, minor, tf, onClose, onDone }) {
  const o = project?.objectives || {}, p = project?.preferences || {}, cs = project?.constraints || {};
  const [f, setF] = useState({
    target_level: o.target_level || "", target_club_type: o.target_club_type || "",
    playing_time: o.playing_time || "", short_term: o.short_term || "", medium_term: o.medium_term || "",
    zones: (p.zones || []).join(", "), salary_min: p.salary_min ?? "", salary_max: p.salary_max ?? "",
    contract_duration: p.contract_duration || "", competitions: p.competitions || "",
    family: cs.family || "", studies: cs.studies || "", relocation: cs.relocation || "",
    language: cs.language || "", other: cs.other || "",
    status: project?.status || "draft", validated_by: project?.validated_by || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/mandats-signes/${pid}/career-project`, {
        objectives: { target_level: f.target_level, target_club_type: f.target_club_type, playing_time: f.playing_time, short_term: f.short_term, medium_term: f.medium_term },
        preferences: { zones: f.zones.split(",").map((z) => z.trim()).filter(Boolean), salary_min: f.salary_min === "" ? null : Number(f.salary_min), salary_max: f.salary_max === "" ? null : Number(f.salary_max), contract_duration: f.contract_duration, competitions: f.competitions },
        constraints: { family: f.family, studies: f.studies, relocation: f.relocation, language: f.language, other: f.other },
        status: f.status, validated_by: f.validated_by,
      });
      toast.success(tf(f.status === "validated" ? "Projet validé" : "Projet enregistré")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Modal title={tf("Projet de carrière")} onClose={onClose} testid="career-project-dialog" wide>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={tf("Niveau visé")}><input className={inputCls} value={f.target_level} onChange={set("target_level")} placeholder={tf("Ligue 2 ou National")} data-testid="cp-target-level" /></Field>
        <Field label={tf("Type de club")}><input className={inputCls} value={f.target_club_type} onChange={set("target_club_type")} data-testid="cp-club-type" /></Field>
        <Field label={tf("Temps de jeu / rôle")}><input className={inputCls} value={f.playing_time} onChange={set("playing_time")} data-testid="cp-playing-time" /></Field>
        <Field label={tf("Objectif court terme")}><input className={inputCls} value={f.short_term} onChange={set("short_term")} data-testid="cp-short-term" /></Field>
        <Field label={tf("Objectif moyen terme")}><input className={inputCls} value={f.medium_term} onChange={set("medium_term")} data-testid="cp-medium-term" /></Field>
        <Field label={tf("Zones acceptées (séparées par des virgules)")}><input className={inputCls} value={f.zones} onChange={set("zones")} placeholder="France, Belgique…" data-testid="cp-zones" /></Field>
        <Field label={tf("Salaire min (€ / mois)")}><input type="number" className={inputCls} value={f.salary_min} onChange={set("salary_min")} data-testid="cp-salary-min" /></Field>
        <Field label={tf("Salaire max (€ / mois)")}><input type="number" className={inputCls} value={f.salary_max} onChange={set("salary_max")} data-testid="cp-salary-max" /></Field>
        <Field label={tf("Durée de contrat souhaitée")}><input className={inputCls} value={f.contract_duration} onChange={set("contract_duration")} data-testid="cp-duration" /></Field>
        <Field label={tf("Compétitions visées")}><input className={inputCls} value={f.competitions} onChange={set("competitions")} data-testid="cp-competitions" /></Field>
        <Field label={tf("Contraintes familiales")}><input className={inputCls} value={f.family} onChange={set("family")} data-testid="cp-family" /></Field>
        <Field label={tf("Études")}><input className={inputCls} value={f.studies} onChange={set("studies")} data-testid="cp-studies" /></Field>
        <Field label={tf("Mobilité / logement")}><input className={inputCls} value={f.relocation} onChange={set("relocation")} data-testid="cp-relocation" /></Field>
        <Field label={tf("Langue")}><input className={inputCls} value={f.language} onChange={set("language")} data-testid="cp-language" /></Field>
        <Field label={tf("Autres contraintes")}><input className={inputCls} value={f.other} onChange={set("other")} data-testid="cp-other" /></Field>
      </div>
      <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-2">
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={f.status === "validated"} onChange={(e) => setF({ ...f, status: e.target.checked ? "validated" : "draft" })} className="accent-[#E11D48]" data-testid="cp-validated-check" />
          {tf(minor ? "Validé par l'athlète et son représentant légal" : "Validé par l'athlète")}
        </label>
        {f.status === "validated" && (
          <Field label={tf(minor ? "Validé par (représentant légal / famille)" : "Validé par")}>
            <input className={inputCls} value={f.validated_by} onChange={set("validated_by")} data-testid="cp-validated-by" />
          </Field>
        )}
      </div>
      <Footer onClose={onClose} onSave={save} busy={busy} tf={tf} testid="cp-save-btn"
        saveLabel={tf(f.status === "validated" ? "Valider le projet" : "Enregistrer le brouillon")} />
    </Modal>
  );
}

export function TargetClubDialog({ pid, tf, onClose, onDone }) {
  const [clubs, setClubs] = useState([]);
  const [q, setQ] = useState("");
  const [clubId, setClubId] = useState(null);
  const [name, setName] = useState("");
  const [league, setLeague] = useState("");
  const [country, setCountry] = useState("France");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/clubs").then((r) => setClubs(Array.isArray(r.data) ? r.data : r.data.clubs || [])).catch(() => setClubs([]));
  }, []);
  const filtered = clubs.filter((c) => c.name?.toLowerCase().includes(q.toLowerCase())).slice(0, 6);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/mandats-signes/${pid}/target-clubs`,
        clubId ? { club_id: clubId } : { club_name: name.trim(), league, country });
      toast.success(tf("Club ajouté aux cibles")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Modal title={tf("Ajouter un club cible")} onClose={onClose} testid="target-club-dialog">
      <Field label={tf("Rechercher un club existant")}>
        <input className={inputCls} value={q} onChange={(e) => { setQ(e.target.value); setClubId(null); }} placeholder={tf("Nom du club…")} data-testid="tc-search" />
      </Field>
      {q && filtered.length > 0 && (
        <ul className="rounded-lg border border-border divide-y divide-border/60 overflow-hidden">
          {filtered.map((c) => (
            <li key={c.id}>
              <button onClick={() => { setClubId(c.id); setQ(c.name); }} data-testid={`tc-pick-${c.id}`}
                className={cn("w-full text-left px-3 py-2 text-xs hover:bg-secondary/60", clubId === c.id && "bg-athlynx/[0.07]")}>
                <span className="font-medium">{c.name}</span>
                <span className="text-muted-foreground"> · {[c.league, c.country].filter(Boolean).join(" · ")}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!clubId && (
        <div className="rounded-xl border border-dashed border-border p-3 space-y-2">
          <p className="text-[11px] text-muted-foreground">{tf("Ou créer un nouveau club (référencé dans Clubs)")}</p>
          <Field label={tf("Nom du club")}><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} data-testid="tc-new-name" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label={tf("Championnat / niveau")}><input className={inputCls} value={league} onChange={(e) => setLeague(e.target.value)} data-testid="tc-new-league" /></Field>
            <Field label={tf("Pays")}><input className={inputCls} value={country} onChange={(e) => setCountry(e.target.value)} data-testid="tc-new-country" /></Field>
          </div>
        </div>
      )}
      <Footer onClose={onClose} onSave={save} busy={busy || (!clubId && !name.trim())} tf={tf} saveLabel={tf("Ajouter")} testid="tc-save-btn" />
    </Modal>
  );
}

export function OutreachLogDialog({ pid, targets, target, tf, onClose, onDone }) {
  const candidates = targets.filter((t) => !["REJECTED"].includes(t.status));
  const [tid, setTid] = useState(target?.id || candidates[0]?.id || null);
  const [channel, setChannel] = useState("email");
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!tid) return;
    setBusy(true);
    try {
      await api.post(`/mandats-signes/${pid}/target-clubs/${tid}/outreach`,
        { channel, note: note.trim(), follow_up_at: followUp || null });
      toast.success(tf("Démarche enregistrée")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Modal title={tf("Proposer à un club")} onClose={onClose} testid="outreach-dialog">
      <p className="text-[11px] text-muted-foreground">{tf("Enregistrement manuel — aucun message n'est envoyé automatiquement par Athlynx.")}</p>
      {candidates.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 text-center">{tf("Ajoutez d'abord un club cible.")}</p>
      ) : (
        <>
          <Field label={tf("Club")}>
            <select className={inputCls} value={tid || ""} onChange={(e) => setTid(e.target.value)} data-testid="outreach-club-select">
              {candidates.map((t) => <option key={t.id} value={t.id}>{t.club_name}</option>)}
            </select>
          </Field>
          <Field label={tf("Canal")}>
            <select className={inputCls} value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="outreach-channel-select">
              <option value="email">Email</option><option value="phone">{tf("Téléphone")}</option>
              <option value="whatsapp">WhatsApp</option><option value="meeting">{tf("Rendez-vous")}</option>
            </select>
          </Field>
          <Field label={tf("Message / résumé de la démarche")}>
            <textarea className={cn(inputCls, "h-20 py-2")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="outreach-note" />
          </Field>
          <Field label={tf("Relance prévue (optionnel)")}>
            <input type="date" className={inputCls} value={followUp} onChange={(e) => setFollowUp(e.target.value)} data-testid="outreach-followup-date" />
          </Field>
          <Footer onClose={onClose} onSave={save} busy={busy} tf={tf} saveLabel={tf("Enregistrer la démarche")} testid="outreach-save-btn" />
        </>
      )}
    </Modal>
  );
}

export function OfferDialog({ pid, targets, offer, tf, onClose, onDone }) {
  const [f, setF] = useState({
    target_club_id: offer?.target_club_id || targets.find((t) => ["PROPOSED", "INTERESTED", "TRIAL"].includes(t.status))?.id || targets[0]?.id || "",
    status: offer?.status || "RECEIVED", offer_date: offer?.offer_date || "", deadline: offer?.deadline || "",
    duration_months: offer?.duration_months ?? "", salary_monthly: offer?.salary_annual ? Math.round(offer.salary_annual / 12) : "",
    signing_bonus: offer?.signing_bonus ?? "", bonuses: offer?.bonuses || "",
    commission_percent: offer?.commission_percent ?? "", agent_fee_payer: offer?.agent_fee_payer || "Club",
    options: offer?.options || "", clauses: offer?.clauses || "", notes: offer?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    const body = {
      offer_date: f.offer_date || null, deadline: f.deadline || null,
      duration_months: f.duration_months === "" ? null : Number(f.duration_months),
      salary_annual: f.salary_monthly === "" ? null : Number(f.salary_monthly) * 12,
      signing_bonus: f.signing_bonus === "" ? null : Number(f.signing_bonus),
      bonuses: f.bonuses, commission_percent: f.commission_percent === "" ? null : Number(f.commission_percent),
      agent_fee_payer: f.agent_fee_payer, options: f.options, clauses: f.clauses, notes: f.notes,
    };
    try {
      if (offer) await api.patch(`/mandats-signes/${pid}/offers/${offer.id}`, body);
      else await api.post(`/mandats-signes/${pid}/offers`, { ...body, target_club_id: f.target_club_id || null, status: "RECEIVED" });
      toast.success(tf(offer ? "Offre mise à jour" : "Offre enregistrée")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Modal title={tf(offer ? "Modifier l'offre" : "Enregistrer une offre")} onClose={onClose} testid="offer-dialog" wide>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {!offer && (
          <Field label={tf("Club")}>
            <select className={inputCls} value={f.target_club_id} onChange={set("target_club_id")} data-testid="offer-club-select">
              {targets.map((t) => <option key={t.id} value={t.id}>{t.club_name}</option>)}
            </select>
          </Field>
        )}
        <Field label={tf("Date de l'offre")}><input type="date" className={inputCls} value={f.offer_date} onChange={set("offer_date")} data-testid="offer-date" /></Field>
        <Field label={tf("Échéance de réponse")}><input type="date" className={inputCls} value={f.deadline} onChange={set("deadline")} data-testid="offer-deadline" /></Field>
        <Field label={tf("Durée (mois)")}><input type="number" className={inputCls} value={f.duration_months} onChange={set("duration_months")} data-testid="offer-duration" /></Field>
        <Field label={tf("Salaire (€ / mois)")}><input type="number" className={inputCls} value={f.salary_monthly} onChange={set("salary_monthly")} data-testid="offer-salary" /></Field>
        <Field label={tf("Prime à la signature (€)")}><input type="number" className={inputCls} value={f.signing_bonus} onChange={set("signing_bonus")} data-testid="offer-bonus" /></Field>
        <Field label={tf("Autres primes")}><input className={inputCls} value={f.bonuses} onChange={set("bonuses")} data-testid="offer-bonuses" /></Field>
        <Field label={tf("Commission (%)")}><input type="number" step="0.5" className={inputCls} value={f.commission_percent} onChange={set("commission_percent")} data-testid="offer-commission" /></Field>
        <Field label={tf("Commission payée par")}><input className={inputCls} value={f.agent_fee_payer} onChange={set("agent_fee_payer")} data-testid="offer-payer" /></Field>
        <Field label={tf("Options")}><input className={inputCls} value={f.options} onChange={set("options")} data-testid="offer-options" /></Field>
        <Field label={tf("Clauses")}><input className={inputCls} value={f.clauses} onChange={set("clauses")} data-testid="offer-clauses" /></Field>
        <Field label={tf("Notes")}><input className={inputCls} value={f.notes} onChange={set("notes")} data-testid="offer-notes" /></Field>
      </div>
      <Footer onClose={onClose} onSave={save} busy={busy || (!offer && !f.target_club_id)} tf={tf} saveLabel={tf("Enregistrer")} testid="offer-save-btn" />
    </Modal>
  );
}

export function FeedbackDialog({ pid, offer, minor, tf, onClose, onDone }) {
  const [who, setWho] = useState("");
  const [verdict, setVerdict] = useState("to_discuss");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/mandats-signes/${pid}/offers/${offer.id}/feedback`, { who: who.trim() || tfr("Athlète"), verdict, note: note.trim() });
      toast.success(tf("Avis enregistré")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Modal title={`${tf("Avis sur l'offre")} — ${offer.club_name}`} onClose={onClose} testid="feedback-dialog">
      <Field label={tf(minor ? "Qui donne l'avis ? (athlète, représentant légal, famille…)" : "Qui donne l'avis ?")}>
        <input className={inputCls} value={who} onChange={(e) => setWho(e.target.value)} placeholder={tf("Athlète")} data-testid="fb-who" />
      </Field>
      <Field label={tf("Avis")}>
        <select className={inputCls} value={verdict} onChange={(e) => setVerdict(e.target.value)} data-testid="fb-verdict">
          <option value="interested">{tf("Intéressé")}</option>
          <option value="to_discuss">{tf("À discuter")}</option>
          <option value="preferred">{tf("Préférence n°1")}</option>
          <option value="waiting">{tf("En attente")}</option>
          <option value="refused">{tf("Refus")}</option>
        </select>
      </Field>
      <Field label={tf("Note")}>
        <textarea className={cn(inputCls, "h-16 py-2")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="fb-note" />
      </Field>
      <Footer onClose={onClose} onSave={save} busy={busy} tf={tf} saveLabel={tf("Enregistrer")} testid="fb-save-btn" />
    </Modal>
  );
}

export function CompareOffersDialog({ offers, tf, onClose, onPlace }) {
  const rows = [
    [tfr("Durée"), (o) => o.duration_months ? `${o.duration_months} ${tf("mois")}` : "—"],
    ["Salaire mensuel", (o) => o.salary_annual ? `${Math.round(o.salary_annual / 12).toLocaleString(dateLocale())} €` : "—"],
    ["Prime signature", (o) => o.signing_bonus ? `${o.signing_bonus.toLocaleString(dateLocale())} €` : "—"],
    ["Autres primes", (o) => o.bonuses || "—"],
    ["Commission", (o) => o.commission_percent != null ? `${o.commission_percent}% (${o.agent_fee_payer || "?"})` : "—"],
    ["Options", (o) => o.options || "—"],
    ["Clauses", (o) => o.clauses || "—"],
    [tfr("Échéance"), (o) => o.deadline ? `${fmtDate(o.deadline)}${o.deadline_days != null ? ` (${o.deadline_days} j)` : ""}` : "—"],
    ["Avis", (o) => (o.feedback || []).length ? tf({ interested: tfr("Intéressé"), to_discuss: tfr("À discuter"), refused: tfr("Refus"), preferred: tfr("Préférence n°1"), waiting: tfr("En attente") }[o.feedback[o.feedback.length - 1].verdict]) : "—"],
  ];
  return (
    <Modal title={tf("Comparer les offres")} onClose={onClose} testid="compare-offers-dialog" wide>
      <p className="text-[11px] text-muted-foreground">{tf("Comparaison factuelle des offres actives — aucune recommandation automatique.")}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 pr-3 text-muted-foreground font-medium w-32" />
              {offers.map((o) => (
                <th key={o.id} className="text-left py-2 px-3 min-w-[150px]">
                  <div className="font-bold text-sm">{o.club_name}</div>
                  <div className="text-[10px] text-muted-foreground font-normal">{tf(o.status_label)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {rows.map(([label, fn]) => (
              <tr key={label}>
                <td className="py-2 pr-3 text-muted-foreground">{tf(label)}</td>
                {offers.map((o) => <td key={o.id} className="py-2 px-3 font-medium">{fn(o)}</td>)}
              </tr>
            ))}
            <tr>
              <td className="py-2 pr-3" />
              {offers.map((o) => (
                <td key={o.id} className="py-2 px-3">
                  <Button size="sm" className="h-7 text-[11px] bg-foreground hover:bg-foreground/90 text-background"
                    onClick={() => onPlace(o)} data-testid={`compare-place-${o.id}`}>
                    {tf("Confirmer le placement")}
                  </Button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

export function TerminateDialog({ pid, name, tf, onClose, onDone }) {
  const [reason, setReason] = useState("expiration");
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/mandats-signes/${pid}/terminate`, { reason, termination_date: date || null, note: note.trim() });
      toast.success(tf("Mandat terminé — client archivé dans « Terminés »")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Modal title={`${tf("Résilier le mandat")} — ${name}`} onClose={onClose} testid="terminate-dialog">
      <p className="text-[11px] text-muted-foreground">
        {tf("Le mandat sera conservé dans l'historique. L'athlète quittera le pipeline actif et restera visible dans l'onglet « Terminés ».")}
      </p>
      <Field label={tf("Motif")}>
        <select className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="term-reason">
          <option value="expiration">{tf("Expiration")}</option>
          <option value="athlete">{tf("Résiliation par l'athlète")}</option>
          <option value="agent">{tf("Résiliation par l'agent")}</option>
          <option value="mutual">{tf("Accord mutuel")}</option>
          <option value="other">{tf("Autre")}</option>
        </select>
      </Field>
      <Field label={tf("Date de fin")}>
        <input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} data-testid="term-date" />
      </Field>
      <Field label={tf("Note (optionnel)")}>
        <textarea className={cn(inputCls, "h-16 py-2")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="term-note" />
      </Field>
      <Footer onClose={onClose} onSave={save} busy={busy} tf={tf} danger saveLabel={tf("Confirmer la fin du mandat")} testid="term-confirm-btn" />
    </Modal>
  );
}

export function CheckinDialog({ pid, tf, onClose, onDone }) {
  const [type, setType] = useState("call");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/mandats-signes/${pid}/checkin`, { type, note: note.trim() });
      toast.success(tf("Échange enregistré")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Modal title={tf("Ajouter un échange")} onClose={onClose} testid="checkin-dialog">
      <Field label={tf("Type")}>
        <select className={inputCls} value={type} onChange={(e) => setType(e.target.value)} data-testid="checkin-type">
          <option value="call">{tf("Point téléphonique")}</option>
          <option value="meeting">{tf("Rendez-vous")}</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="email">Email</option>
          <option value="video">{tf("Visio")}</option>
        </select>
      </Field>
      <Field label={tf("Résumé")}>
        <textarea className={cn(inputCls, "h-20 py-2")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="checkin-note" />
      </Field>
      <Footer onClose={onClose} onSave={save} busy={busy} tf={tf} saveLabel={tf("Enregistrer")} testid="checkin-save-btn" />
    </Modal>
  );
}

export function ScheduleCheckinDialog({ pid, tf, onClose, onDone }) {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/mandats-signes/${pid}/schedule-checkin`, { date, note: note.trim() || "Point client" });
      toast.success(tf("Point programmé — visible dans À relancer")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Modal title={tf("Programmer un point client")} onClose={onClose} testid="schedule-checkin-dialog">
      <Field label={tf("Date")}>
        <input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} data-testid="sched-date" />
      </Field>
      <Field label={tf("Objet")}>
        <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder={tf("Point client")} data-testid="sched-note" />
      </Field>
      <Footer onClose={onClose} onSave={save} busy={busy || !date} tf={tf} saveLabel={tf("Programmer")} testid="sched-save-btn" />
    </Modal>
  );
}
